// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview One-time migration from legacy dedicated collections to the
 * generic `records` collection introduced by the schema-in-db consolidation.
 *
 * The 62 legacy domain collections (deals, contacts, campaigns, etc.) are
 * replaced by 13 primitive collections discriminated by `schemaId`. This
 * module provides the migration utilities to move existing data.
 */

import { createId } from '../utils/id';
import type { SchemaModel } from '../models/Schema';
import type { RecordModel } from '../models/Record';

/**
 * Result of a migration run for a single collection.
 */
export interface MigrationResult {
  /** The schema name (== legacy collection name) that was migrated. */
  collection: string;
  /** Number of documents successfully written to `records`. */
  migrated: number;
  /** Number of documents that failed to insert. */
  errors: number;
  /** Per-document error details for failed inserts. */
  errorDetails: Array<{ id: string; error: string }>;
}

/**
 * Top-level fields that carry context about the document origin.
 * These are never copied into the `data` bag.
 */
const CONTEXT_FIELDS = new Set([
  'id',
  'orgId',
  'workspaceId',
  'userId',
  'createdAt',
  'updatedAt',
  '_rev',
  '_meta',
  '_deleted',
  '_attachments',
]);

/**
 * Fields that are promoted to top-level `RecordModel` properties rather than
 * being stored in the `data` bag. The record schema has first-class columns
 * for these, enabling indexed queries without unpacking the data blob.
 */
const PROMOTED_FIELDS = new Set([
  'title',
  'status',
  'parentId',
  'parentCollection',
  'archived',
  'completed',
  'completedAt',
  'startedAt',
]);

/**
 * Migrate all documents from a legacy collection into the records collection.
 *
 * For each document:
 * 1. Map promoted fields (`title`, `status`, etc.) to top-level RecordModel fields
 * 2. Map remaining domain fields into the `data` bag
 * 3. Preserve `orgId`, `workspaceId`, `userId`, timestamps
 * 4. Set `schemaId` from the provided schema definition
 * 5. Generate a new record ID (`rec_` prefix)
 *
 * Idempotent: skips documents that already exist in `records` with a matching
 * `metadata.sourceId`. The source ID is stored in `metadata.sourceId` for
 * traceability and to make repeated runs safe.
 *
 * @param legacyCollection - The source RxDB collection (or compatible mock).
 * @param recordsCollection - The target `records` RxDB collection.
 * @param schema - The SchemaModel that governs the migrated entity type.
 * @returns Counts and per-document error details for the migration run.
 */
export async function migrateCollectionToRecords(
  legacyCollection: {
    find(query: { selector: Record<string, unknown> }): { exec(): Promise<unknown[]> };
  },
  recordsCollection: {
    findOne(query: { selector: Record<string, unknown> }): { exec(): Promise<unknown> };
    insert(doc: RecordModel): Promise<unknown>;
  },
  schema: SchemaModel,
): Promise<MigrationResult> {
  let migrated = 0;
  let errors = 0;
  const errorDetails: Array<{ id: string; error: string }> = [];

  // Fetch all documents from the legacy collection in one pass.
  // RxDB queries are local, so there is no pagination concern here.
  const docs = await legacyCollection.find({ selector: {} }).exec();

  for (const doc of docs) {
    // Normalise: RxDB documents expose toJSON(); plain objects do not.
    const legacy = (typeof (doc as { toJSON?: () => Record<string, unknown> }).toJSON === 'function')
      ? (doc as { toJSON: () => Record<string, unknown> }).toJSON()
      : (doc as Record<string, unknown>);

    const sourceId = (legacy['id'] as string | undefined) ?? 'unknown';

    try {
      // Idempotency check: skip if a record with this sourceId already exists.
      const existing = await recordsCollection.findOne({
        selector: {
          schemaId: schema.id,
          'metadata.sourceId': sourceId,
        },
      }).exec();

      if (existing) {
        continue;
      }

      // All fields not in CONTEXT_FIELDS or PROMOTED_FIELDS go into data bag.
      const data: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(legacy)) {
        if (!CONTEXT_FIELDS.has(key) && !PROMOTED_FIELDS.has(key)) {
          data[key] = value;
        }
      }

      const now = new Date().toISOString();

      const record: RecordModel = {
        id: createId('records'),
        schemaId: schema.id,
        orgId: (legacy['orgId'] as string | undefined) ?? '',
        workspaceId: (legacy['workspaceId'] as string | undefined) ?? '',
        // Fall back to empty string when no userId is present.
        // Empty string is explicit about the absence of an owner and does not
        // pollute audit queries with a placeholder value.
        userId: (legacy['userId'] as string | undefined) ?? '',
        title: (legacy['title'] as string | undefined)
          ?? (legacy['name'] as string | undefined)
          ?? '',
        status: (legacy['status'] as string | undefined) ?? '',
        parentId: (legacy['parentId'] as string | undefined) ?? '',
        assigneeId: (legacy['assigneeId'] as string | undefined) ?? '',
        archived: (legacy['archived'] as boolean | undefined) ?? false,
        version: 1,
        data,
        metadata: {
          sourceId,
          sourceCollection: schema.name,
          migratedAt: now,
        },
        createdAt: (legacy['createdAt'] as string | undefined) ?? now,
        updatedAt: (legacy['updatedAt'] as string | undefined) ?? now,
      };

      // Conditionally set optional promoted fields only when present in source.
      if (legacy['parentId'] !== undefined) {
        record.parentId = legacy['parentId'] as string;
      }
      if (legacy['parentCollection'] !== undefined) {
        record.parentCollection = legacy['parentCollection'] as string;
      }
      if (legacy['completed'] !== undefined) {
        record.completed = legacy['completed'] as boolean;
      }
      if (legacy['completedAt'] !== undefined) {
        record.completedAt = legacy['completedAt'] as string;
      }
      if (legacy['startedAt'] !== undefined) {
        record.startedAt = legacy['startedAt'] as string;
      }

      await recordsCollection.insert(record);
      migrated++;
    } catch (error) {
      errors++;
      errorDetails.push({
        id: sourceId,
        error: (error as Error).message,
      });
    }
  }

  return {
    collection: schema.name,
    migrated,
    errors,
    errorDetails,
  };
}

/**
 * Run migration for multiple legacy collections in sequence.
 *
 * Each mapping entry pairs a legacy collection with the SchemaModel it should
 * be migrated under. The `recordsCollection` is shared across all migrations.
 *
 * Errors within a single collection do not abort other collections; per-item
 * errors are captured inside each {@link MigrationResult}.
 *
 * @param mappings - Array of `{ name, legacyCollection, schema }` descriptors.
 * @param recordsCollection - The shared target `records` RxDB collection.
 * @returns One {@link MigrationResult} per mapping entry, in order.
 */
export async function migrateAll(
  mappings: Array<{
    /** Display name used for logging; typically matches schema.name. */
    name: string;
    legacyCollection: {
      find(query: { selector: Record<string, unknown> }): { exec(): Promise<unknown[]> };
    };
    schema: SchemaModel;
  }>,
  recordsCollection: {
    findOne(query: { selector: Record<string, unknown> }): { exec(): Promise<unknown> };
    insert(doc: RecordModel): Promise<unknown>;
  },
): Promise<MigrationResult[]> {
  const results: MigrationResult[] = [];

  for (const mapping of mappings) {
    const result = await migrateCollectionToRecords(
      mapping.legacyCollection,
      recordsCollection,
      mapping.schema,
    );
    results.push(result);
  }

  return results;
}
